import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Modal,
  TextInput,
  ActivityIndicator,
  Alert,
  Dimensions,
  Image,
  Animated,
  Platform,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function ScheduleFirstDayScreen({ navigation, route }) {
  const { candidate } = route.params;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date(candidate.startDate || Date.now()));
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [selectedLocation, setSelectedLocation] = useState('Office');
  const [selectedManager, setSelectedManager] = useState(candidate.onboardingManager || '');
  const [selectedBuddy, setSelectedBuddy] = useState(candidate.onboardingBuddy || '');
  const [agenda, setAgenda] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [showBuddyModal, setShowBuddyModal] = useState(false);
  const [showAgendaModal, setShowAgendaModal] = useState(false);


  // ── Company logo loader (auto-injected) ─────────────────────────────────────
  // Hydrates from cache for instant paint, then refreshes from
  // /api/company-profile/:id so any logo / name change made elsewhere
  // (Settings, Dashboard, etc.) propagates to this screen on next open.
  const [_companyLogo,  _setCompanyLogo]  = useState(null);
  const [_companyName,  _setCompanyName]  = useState('');
  useEffect(() => { _loadCompanyLogo(); }, []);
  const _loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        _setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) _setCompanyName(profile.name || profile.company_name);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        try {
          const res = await fetch(`https://api.jobeng.ai/api/company-profile/${cid}`, {
            headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' }
          });
          if (res.ok) {
            const json = await res.json();
            const row = (json?.data && !Array.isArray(json.data)) ? json.data
                     : (Array.isArray(json?.data) ? json.data[0] : json);
            if (row && (row.logo || row.name || row.company_name)) {
              if (row.logo) _setCompanyLogo(row.logo);
              const nm = row.name || row.company_name;
              if (nm) _setCompanyName(nm);
              await AsyncStorage.setItem('company_profile', JSON.stringify(row));
              console.log('[CompanyLogo] refreshed from API for cid=' + cid);
            }
          }
        } catch (e) { /* offline — cache is enough */ }
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };
  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Available time slots
  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
  ];

  // Location options
  const locationOptions = [
    { id: 'office', name: 'Office', icon: 'office-building', description: 'Main office location' },
    { id: 'remote', name: 'Remote', icon: 'home', description: 'Work from home setup' },
    { id: 'hybrid', name: 'Hybrid', icon: 'laptop', description: 'Mix of office and remote' },
    { id: 'client_site', name: 'Client Site', icon: 'domain', description: 'At client location' },
  ];

  // Available managers
  const availableManagers = [
    { id: '1', name: 'Sarah Johnson', role: 'Engineering Manager', avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=random' },
    { id: '2', name: 'Michael Chen', role: 'Product Manager', avatar: 'https://ui-avatars.com/api/?name=Michael+Chen&background=random' },
    { id: '3', name: 'Emma Williams', role: 'Team Lead', avatar: 'https://ui-avatars.com/api/?name=Emma+Williams&background=random' },
    { id: '4', name: 'James Rodriguez', role: 'Senior Manager', avatar: 'https://ui-avatars.com/api/?name=James+Rodriguez&background=random' },
  ];

  // Available buddies
  const availableBuddies = [
    { id: '1', name: 'Alex Thompson', role: 'Senior Developer', avatar: 'https://ui-avatars.com/api/?name=Alex+Thompson&background=random' },
    { id: '2', name: 'Lisa Park', role: 'UX Designer', avatar: 'https://ui-avatars.com/api/?name=Lisa+Park&background=random' },
    { id: '3', name: 'David Kim', role: 'DevOps Engineer', avatar: 'https://ui-avatars.com/api/?name=David+Kim&background=random' },
    { id: '4', name: 'Sophie Taylor', role: 'Product Designer', avatar: 'https://ui-avatars.com/api/?name=Sophie+Taylor&background=random' },
  ];

  // Default agenda items
  const defaultAgendaItems = [
    { id: '1', time: '09:00', activity: 'Welcome & Office Tour', duration: '30 min', completed: false },
    { id: '2', time: '09:30', activity: 'HR Orientation', duration: '60 min', completed: false },
    { id: '3', time: '10:30', activity: 'Team Introductions', duration: '45 min', completed: false },
    { id: '4', time: '11:15', activity: 'Coffee Break', duration: '15 min', completed: false },
    { id: '5', time: '11:30', activity: 'IT Setup & Equipment', duration: '60 min', completed: false },
    { id: '6', time: '12:30', activity: 'Lunch with Team', duration: '60 min', completed: false },
    { id: '7', time: '13:30', activity: 'Project Overview', duration: '45 min', completed: false },
    { id: '8', time: '14:15', activity: 'Buddy System Introduction', duration: '30 min', completed: false },
    { id: '9', time: '14:45', activity: 'First Tasks Assignment', duration: '45 min', completed: false },
    { id: '10', time: '15:30', activity: 'Q&A Session', duration: '30 min', completed: false },
  ];

  const [agendaItems, setAgendaItems] = useState(defaultAgendaItems);

  useEffect(() => {
    startAnimations();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatShortDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getNextWeekdays = () => {
    const dates = [];
    const today = new Date();
    let currentDate = new Date(today);
    
    // Start from tomorrow
    currentDate.setDate(currentDate.getDate() + 1);
    
    while (dates.length < 14) {
      // Only include weekdays (Monday to Friday)
      if (currentDate.getDay() >= 1 && currentDate.getDay() <= 5) {
        dates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const handleScheduleFirstDay = async () => {
    setIsLoading(true);
    
    try {
      const scheduleData = {
        candidateId: candidate.id,
        candidateName: candidate.name,
        date: selectedDate.toISOString(),
        time: selectedTime,
        location: selectedLocation,
        manager: selectedManager,
        buddy: selectedBuddy,
        agenda: agendaItems,
        specialRequirements: specialRequirements,
        scheduledAt: new Date().toISOString(),
      };

      // Save via API and cache locally
      try {
        await apiRequest('first-day-schedules', {
          method: 'POST',
          body: { candidate_id: candidate.id, ...scheduleData }
        });
      } catch (apiErr) {
        console.warn('API save failed, caching locally:', apiErr.message);
      }
      await AsyncStorage.setItem(
        `first_day_schedule_${candidate.id}`,
        JSON.stringify(scheduleData)
      );

      Alert.alert(
        'Success!',
        `First day scheduled for ${candidate.name} on ${formatDate(selectedDate)} at ${selectedTime}`,
        [
          {
            text: 'OK',
            onPress: () => {
              navigation.goBack();
            }
          }
        ]
      );
    } catch (error) {
      console.error('Error scheduling first day:', error);
      Alert.alert('Error', 'Failed to schedule first day. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setShowTimePicker(false);
  };

  const handleManagerSelect = (manager) => {
    setSelectedManager(manager.name);
    setShowManagerModal(false);
  };

  const handleBuddySelect = (buddy) => {
    setSelectedBuddy(buddy.name);
    setShowBuddyModal(false);
  };

  const toggleAgendaItem = (itemId) => {
    setAgendaItems(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, completed: !item.completed }
          : item
      )
    );
  };

  const getLocationIcon = (locationId) => {
    const location = locationOptions.find(loc => loc.id === locationId.toLowerCase());
    return location ? location.icon : 'map-marker';
  };

  const getLocationDescription = (locationId) => {
    const location = locationOptions.find(loc => loc.id === locationId.toLowerCase());
    return location ? location.description : 'Location details';
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#3B82F6" />
      
      {/* Header with gradient - matching AcceptedOfferDetailsScreen style */}
      <LinearGradient colors={['#3B82F6', '#2563EB']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <Text style={styles.headerTitle}>Schedule First Day</Text>
            <Text style={styles.headerSubtitle}>{candidate.name}</Text>
          </View>
          
          <TouchableOpacity
            style={styles.headerAction}
            onPress={() => setShowAgendaModal(true)}
          >
            <MaterialCommunityIcons name="calendar-edit" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      <Animated.View 
        style={[
          styles.content,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }]
          }
        ]}
      >
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
          
          {/* Candidate Summary Card */}
          <View style={styles.candidateCard}>
            <View style={styles.candidateHeader}>
              <Image source={{ uri: (candidate.avatar && candidate.avatar.startsWith('http')) ? candidate.avatar : undefined }} style={styles.avatar} />
              <View style={styles.candidateInfo}>
                <Text style={styles.candidateName}>{candidate.name}</Text>
                <Text style={styles.candidatePosition}>{candidate.position}</Text>
                <Text style={styles.candidateCompany}>{candidate.department}</Text>
                <View style={styles.candidateContact}>
                  <MaterialCommunityIcons name="calendar-start" size={14} color="#3B82F6" />
                  <Text style={styles.contactText}>
                    Original Start: {formatShortDate(new Date(candidate.startDate))}
                  </Text>
                </View>
              </View>
              <View style={styles.statusContainer}>
                <View style={[styles.statusBadge, { backgroundColor: '#3B82F6' }]}>
                  <Text style={styles.statusText}>Scheduling</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Date Selection */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <MaterialCommunityIcons name="calendar" size={24} color="#3B82F6" />
              <Text style={styles.sectionTitle}>Select Date</Text>
            </View>
            
            <TouchableOpacity
              style={styles.selectedDateContainer}
              onPress={() => setShowDatePicker(true)}
            >
              <View style={styles.selectedDateInfo}>
                <Text style={styles.selectedDateText}>{formatDate(selectedDate)}</Text>
                <Text style={styles.selectedDateSubtext}>
                  {selectedDate.getTime() === new Date(candidate.startDate).getTime() 
                    ? 'Original start date' 
                    : 'Custom date selected'}
                </Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={24} color="#6B7280" />
            </TouchableOpacity>

            {showDatePicker && (
              <View style={styles.datePickerContainer}>
                <Text style={styles.datePickerTitle}>Available Dates</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.dateScroll}>
                  {getNextWeekdays().map((date, index) => (
                    <TouchableOpacity
                      key={index}
                      style={[
                        styles.dateOption,
                        selectedDate.toDateString() === date.toDateString() && styles.selectedDateOption
                      ]}
                      onPress={() => handleDateSelect(date)}
                    >
                      <Text style={[
                        styles.dateOptionDay,
                        selectedDate.toDateString() === date.toDateString() && styles.selectedDateOptionText
                      ]}>
                        {date.toLocaleDateString('en-GB', { weekday: 'short' })}
                      </Text>
                      <Text style={[
                        styles.dateOptionDate,
                        selectedDate.toDateString() === date.toDateString() && styles.selectedDateOptionText
                      ]}>
                        {date.getDate()}
                      </Text>
                      <Text style={[
                        styles.dateOptionMonth,
                        selectedDate.toDateString() === date.toDateString() && styles.selectedDateOptionText
                      ]}>
                        {date.toLocaleDateString('en-GB', { month: 'short' })}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>

          {/* Time Selection */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <MaterialCommunityIcons name="clock" size={24} color="#10B981" />
              <Text style={styles.sectionTitle}>Select Time</Text>
            </View>
            
            <TouchableOpacity
              style={styles.selectedTimeContainer}
              onPress={() => setShowTimePicker(!showTimePicker)}
            >
              <View style={styles.selectedTimeInfo}>
                <Text style={styles.selectedTimeText}>{selectedTime}</Text>
                <Text style={styles.selectedTimeSubtext}>Start time</Text>
              </View>
              <MaterialCommunityIcons 
                name={showTimePicker ? "chevron-up" : "chevron-down"} 
                size={24} 
                color="#6B7280" 
              />
            </TouchableOpacity>

            {showTimePicker && (
              <View style={styles.timePickerContainer}>
                <View style={styles.timeGrid}>
                  {timeSlots.map((time, index) => (
                    <TouchableOpacity
                      key={index}
                      style={[
                        styles.timeOption,
                        selectedTime === time && styles.selectedTimeOption
                      ]}
                      onPress={() => handleTimeSelect(time)}
                    >
                      <Text style={[
                        styles.timeOptionText,
                        selectedTime === time && styles.selectedTimeOptionText
                      ]}>
                        {time}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
          </View>

          {/* Location Selection */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <MaterialCommunityIcons name="map-marker" size={24} color="#8B5CF6" />
              <Text style={styles.sectionTitle}>Work Location</Text>
            </View>
            
            <View style={styles.locationGrid}>
              {locationOptions.map((location) => (
                <TouchableOpacity
                  key={location.id}
                  style={[
                    styles.locationOption,
                    selectedLocation.toLowerCase() === location.id && styles.selectedLocationOption
                  ]}
                  onPress={() => setSelectedLocation(location.name)}
                >
                  <MaterialCommunityIcons 
                    name={location.icon} 
                    size={24} 
                    color={selectedLocation.toLowerCase() === location.id ? '#FFFFFF' : '#8B5CF6'} 
                  />
                  <Text style={[
                    styles.locationOptionText,
                    selectedLocation.toLowerCase() === location.id && styles.selectedLocationOptionText
                  ]}>
                    {location.name}
                  </Text>
                  <Text style={[
                    styles.locationOptionDescription,
                    selectedLocation.toLowerCase() === location.id && styles.selectedLocationOptionText
                  ]}>
                    {location.description}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Manager & Buddy Selection */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <MaterialCommunityIcons name="account-group" size={24} color="#F59E0B" />
              <Text style={styles.sectionTitle}>Team Assignment</Text>
            </View>
            
            <View style={styles.teamAssignmentContainer}>
              <TouchableOpacity
                style={styles.teamMemberContainer}
                onPress={() => setShowManagerModal(true)}
              >
                <View style={styles.teamMemberInfo}>
                  <MaterialCommunityIcons name="account-tie" size={20} color="#F59E0B" />
                  <View style={styles.teamMemberDetails}>
                    <Text style={styles.teamMemberLabel}>Manager</Text>
                    <Text style={styles.teamMemberName}>{selectedManager || 'Select Manager'}</Text>
                  </View>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#6B7280" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.teamMemberContainer}
                onPress={() => setShowBuddyModal(true)}
              >
                <View style={styles.teamMemberInfo}>
                  <MaterialCommunityIcons name="account-heart" size={20} color="#10B981" />
                  <View style={styles.teamMemberDetails}>
                    <Text style={styles.teamMemberLabel}>Buddy</Text>
                    <Text style={styles.teamMemberName}>{selectedBuddy || 'Select Buddy'}</Text>
                  </View>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#6B7280" />
              </TouchableOpacity>
            </View>
          </View>

          {/* First Day Agenda Preview */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <MaterialCommunityIcons name="clipboard-list" size={24} color="#EF4444" />
              <Text style={styles.sectionTitle}>First Day Agenda</Text>
              <TouchableOpacity
                style={styles.editAgendaButton}
                onPress={() => setShowAgendaModal(true)}
              >
                <MaterialCommunityIcons name="pencil" size={16} color="#EF4444" />
                <Text style={styles.editAgendaText}>Edit</Text>
              </TouchableOpacity>
            </View>
            
            <View style={styles.agendaPreview}>
              {agendaItems.slice(0, 4).map((item, index) => (
                <View key={item.id} style={styles.agendaPreviewItem}>
                  <Text style={styles.agendaPreviewTime}>{item.time}</Text>
                  <View style={styles.agendaPreviewContent}>
                    <Text style={styles.agendaPreviewActivity}>{item.activity}</Text>
                    <Text style={styles.agendaPreviewDuration}>{item.duration}</Text>
                  </View>
                </View>
              ))}
              {agendaItems.length > 4 && (
                <TouchableOpacity
                  style={styles.viewMoreAgenda}
                  onPress={() => setShowAgendaModal(true)}
                >
                  <Text style={styles.viewMoreAgendaText}>
                    View all {agendaItems.length} activities
                  </Text>
                  <MaterialCommunityIcons name="chevron-right" size={16} color="#6B7280" />
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Special Requirements */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <MaterialCommunityIcons name="note-text" size={24} color="#06B6D4" />
              <Text style={styles.sectionTitle}>Special Requirements</Text>
            </View>
            
            <TextInput
              style={styles.requirementsInput}
              value={specialRequirements}
              onChangeText={setSpecialRequirements}
              placeholder="Any special requirements or notes for the first day..."
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          {/* Schedule Summary */}
          <View style={styles.summaryCard}>
            <View style={styles.summaryHeader}>
              <MaterialCommunityIcons name="calendar-check" size={24} color="#10B981" />
              <Text style={styles.summaryTitle}>Schedule Summary</Text>
            </View>
            
            <View style={styles.summaryDetails}>
              <View style={styles.summaryItem}>
                <MaterialCommunityIcons name="calendar" size={16} color="#6B7280" />
                <Text style={styles.summaryLabel}>Date:</Text>
                <Text style={styles.summaryValue}>{formatShortDate(selectedDate)}</Text>
              </View>
              <View style={styles.summaryItem}>
                <MaterialCommunityIcons name="clock" size={16} color="#6B7280" />
                <Text style={styles.summaryLabel}>Time:</Text>
                <Text style={styles.summaryValue}>{selectedTime}</Text>
              </View>
              <View style={styles.summaryItem}>
                <MaterialCommunityIcons name={getLocationIcon(selectedLocation)} size={16} color="#6B7280" />
                <Text style={styles.summaryLabel}>Location:</Text>
                <Text style={styles.summaryValue}>{selectedLocation}</Text>
              </View>
              <View style={styles.summaryItem}>
                <MaterialCommunityIcons name="account-tie" size={16} color="#6B7280" />
                <Text style={styles.summaryLabel}>Manager:</Text>
                <Text style={styles.summaryValue}>{selectedManager || 'Not assigned'}</Text>
              </View>
              <View style={styles.summaryItem}>
                <MaterialCommunityIcons name="account-heart" size={16} color="#6B7280" />
                <Text style={styles.summaryLabel}>Buddy:</Text>
                <Text style={styles.summaryValue}>{selectedBuddy || 'Not assigned'}</Text>
              </View>
            </View>
          </View>

          {/* Action Buttons */}
          <View style={styles.actionsContainer}>
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={handleScheduleFirstDay}
              disabled={isLoading || !selectedManager || !selectedBuddy}
            >
              {isLoading ? (
                <ActivityIndicator color="#FFFFFF" size={20} />
              ) : (
                <>
                  <MaterialCommunityIcons name="calendar-check" size={20} color="#FFFFFF" />
                  <Text style={styles.primaryButtonText}>Schedule First Day</Text>
                </>
              )}
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={() => navigation.goBack()}
            >
              <MaterialCommunityIcons name="cancel" size={18} color="#6B7280" />
              <Text style={styles.secondaryButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </Animated.View>

      {/* Manager Selection Modal */}
      <Modal
        visible={showManagerModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowManagerModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Select Manager</Text>
            <TouchableOpacity onPress={() => setShowManagerModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            {availableManagers.map((manager) => (
              <TouchableOpacity
                key={manager.id}
                style={styles.personOption}
                onPress={() => handleManagerSelect(manager)}
              >
                <Image source={{ uri: (manager.avatar && manager.avatar.startsWith('http')) ? manager.avatar : undefined }} style={styles.personAvatar} />
                <View style={styles.personInfo}>
                  <Text style={styles.personName}>{manager.name}</Text>
                  <Text style={styles.personRole}>{manager.role}</Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#6B7280" />
              </TouchableOpacity>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Buddy Selection Modal */}
      <Modal
        visible={showBuddyModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowBuddyModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Select Buddy</Text>
            <TouchableOpacity onPress={() => setShowBuddyModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            {availableBuddies.map((buddy) => (
              <TouchableOpacity
                key={buddy.id}
                style={styles.personOption}
                onPress={() => handleBuddySelect(buddy)}
              >
                <Image source={{ uri: (buddy.avatar && buddy.avatar.startsWith('http')) ? buddy.avatar : undefined }} style={styles.personAvatar} />
                <View style={styles.personInfo}>
                  <Text style={styles.personName}>{buddy.name}</Text>
                  <Text style={styles.personRole}>{buddy.role}</Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#6B7280" />
              </TouchableOpacity>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Agenda Modal */}
      <Modal
        visible={showAgendaModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowAgendaModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>First Day Agenda</Text>
            <TouchableOpacity onPress={() => setShowAgendaModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent}>
            <Text style={styles.agendaModalSubtitle}>
              Customize the first day schedule for {candidate.name}
            </Text>
            
            <View style={styles.agendaList}>
              {agendaItems.map((item, index) => (
                <View key={item.id} style={styles.agendaItem}>
                  <TouchableOpacity
                    style={styles.agendaCheckbox}
                    onPress={() => toggleAgendaItem(item.id)}
                  >
                    <MaterialCommunityIcons
                      name={item.completed ? 'checkbox-marked' : 'checkbox-blank-outline'}
                      size={24}
                      color={item.completed ? '#10B981' : '#D1D5DB'}
                    />
                  </TouchableOpacity>
                  
                  <View style={styles.agendaItemContent}>
                    <View style={styles.agendaItemHeader}>
                      <Text style={styles.agendaItemTime}>{item.time}</Text>
                      <Text style={styles.agendaItemDuration}>{item.duration}</Text>
                    </View>
                    <Text style={[
                      styles.agendaItemActivity,
                      { textDecorationLine: item.completed ? 'line-through' : 'none' }
                    ]}>
                      {item.activity}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  header: {
    paddingTop: 10,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    marginTop: 2,
  },
  headerAction: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  content: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: -10,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 40,
  },
  candidateCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  candidateHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  candidateInfo: {
    flex: 1,
  },
  candidateName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  candidatePosition: {
    fontSize: 16,
    color: '#6B7280',
    marginBottom: 2,
  },
  candidateCompany: {
    fontSize: 14,
    color: '#8B5CF6',
    fontWeight: '500',
    marginBottom: 8,
  },
  candidateContact: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contactText: {
    marginLeft: 6,
    fontSize: 12,
    color: '#6B7280',
  },
  statusContainer: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  sectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  selectedDateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  selectedDateInfo: {
    flex: 1,
  },
  selectedDateText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  selectedDateSubtext: {
    fontSize: 12,
    color: '#6B7280',
  },
  datePickerContainer: {
    marginTop: 16,
  },
  datePickerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  dateScroll: {
    flexDirection: 'row',
  },
  dateOption: {
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 12,
    marginRight: 8,
    minWidth: 60,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  selectedDateOption: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  dateOptionDay: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  dateOptionDate: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 2,
  },
  dateOptionMonth: {
    fontSize: 10,
    color: '#6B7280',
  },
  selectedDateOptionText: {
    color: '#FFFFFF',
  },
  selectedTimeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  selectedTimeInfo: {
    flex: 1,
  },
  selectedTimeText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 2,
  },
  selectedTimeSubtext: {
    fontSize: 12,
    color: '#6B7280',
  },
  timePickerContainer: {
    marginTop: 16,
  },
  timeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  timeOption: {
    width: '23%',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  selectedTimeOption: {
    backgroundColor: '#10B981',
    borderColor: '#10B981',
  },
  timeOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  selectedTimeOptionText: {
    color: '#FFFFFF',
  },
  locationGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  locationOption: {
    width: '48%',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  selectedLocationOption: {
    backgroundColor: '#8B5CF6',
    borderColor: '#8B5CF6',
  },
  locationOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginTop: 8,
    marginBottom: 4,
  },
  locationOptionDescription: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  selectedLocationOptionText: {
    color: '#FFFFFF',
  },
  teamAssignmentContainer: {
    gap: 12,
  },
  teamMemberContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  teamMemberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  teamMemberDetails: {
    marginLeft: 12,
  },
  teamMemberLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  teamMemberName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  editAgendaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  editAgendaText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#EF4444',
    fontWeight: '500',
  },
  agendaPreview: {
    gap: 12,
  },
  agendaPreviewItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  agendaPreviewTime: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
    width: 60,
  },
  agendaPreviewContent: {
    flex: 1,
    marginLeft: 12,
  },
  agendaPreviewActivity: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 2,
  },
  agendaPreviewDuration: {
    fontSize: 12,
    color: '#6B7280',
  },
  viewMoreAgenda: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  viewMoreAgendaText: {
    fontSize: 14,
    color: '#6B7280',
    marginRight: 4,
  },
  requirementsInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 12,
    padding: 16,
    fontSize: 14,
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
    minHeight: 80,
  },
  summaryCard: {
    backgroundColor: '#ECFDF5',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#D1FAE5',
  },
  summaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  summaryTitle: {
    marginLeft: 8,
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  summaryDetails: {
    gap: 8,
  },
  summaryItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  summaryLabel: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    color: '#6B7280',
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  actionsContainer: {
    gap: 12,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3B82F6',
    paddingVertical: 16,
    borderRadius: 12,
    shadowColor: '#3B82F6',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  primaryButtonText: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  secondaryButtonText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  personOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  personAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 16,
  },
  personInfo: {
    flex: 1,
  },
  personName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  personRole: {
    fontSize: 14,
    color: '#6B7280',
  },
  agendaModalSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 20,
    textAlign: 'center',
  },
  agendaList: {
    gap: 12,
  },
  agendaItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  agendaCheckbox: {
    marginRight: 12,
    marginTop: 2,
  },
  agendaItemContent: {
    flex: 1,
  },
  agendaItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  agendaItemTime: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#3B82F6',
  },
  agendaItemDuration: {
    fontSize: 12,
    color: '#6B7280',
  },
  agendaItemActivity: {
    fontSize: 14,
    color: '#1F2937',
  },
});
